import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface RequesterStatsTableProps {
  requesters: {
    user_id: string;
    full_name: string;
    email: string;
    created_at: string;
    last_login: string;
    num_requests: number;
  }[];
  loading: boolean;
  onDelete: (ids: string[]) => Promise<void>;
}

export const RequesterStatsTable: React.FC<RequesterStatsTableProps> = ({
  requesters,
  loading,
  onDelete,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(requesters.map(r => r.user_id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    setSelected(newSelected);
  };

  const handleDelete = async () => {
    try {
      await onDelete(selected);
      setSelected([]);
      setConfirmOpen(false);
    } catch (error) {
      console.error('Failed to delete requesters:', error);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {selected.length > 0 && (
        <Button
          variant="contained"
          color="error"
          onClick={() => setConfirmOpen(true)}
          sx={{ mb: 2 }}
        >
          Delete Selected
        </Button>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < requesters.length}
                  checked={selected.length === requesters.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Number of Requests</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requesters.map((requester) => (
              <TableRow
                key={requester.user_id}
                selected={selected.includes(requester.user_id)}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(requester.user_id)}
                    onChange={() => handleSelect(requester.user_id)}
                  />
                </TableCell>
                <TableCell>{requester.user_id}</TableCell>
                <TableCell>{requester.full_name}</TableCell>
                <TableCell>{requester.email}</TableCell>
                <TableCell>{new Date(requester.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(requester.last_login).toLocaleDateString()}</TableCell>
                <TableCell>{requester.num_requests}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected requesters?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequesterStatsTable;
